/**
 * accounting_export_runs + accounting_export_run_invoices — one row per QuickBooks
 * Desktop export file produced, and the invoices it contained.
 *
 * DEC-326 item 6 / migration 0137. See the migration header for the full why.
 *
 * `accounting_export_email_log` (0084) recorded only SCHEDULED email runs, as
 * counts, and the ON_DEMAND download recorded nothing. These tables cover both
 * paths and keep WHICH invoices went out, so "not yet exported" is a set
 * difference. A SCHEDULED run points at its email log row, which still owns the
 * idempotency guarantee and the sent/failed counters.
 */

import { sql } from 'drizzle-orm';
import {
  date,
  index,
  integer,
  pgPolicy,
  pgTable,
  primaryKey,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core';
import { accountingExportEmailLog } from './accounting-export-email-log.js';
import { appUserRole } from './enums.js';
import { invoices } from './invoices.js';
import { tenants, users } from './users.js';

export const accountingExportRuns = pgTable(
  'accounting_export_runs',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    /** 'ON_DEMAND' | 'SCHEDULED' — constrained by a DB CHECK in migration 0137. */
    source: text('source').notNull(),
    periodStart: date('period_start').notNull(),
    periodEnd: date('period_end').notNull(),
    invoiceCount: integer('invoice_count').notNull().default(0),
    lineCount: integer('line_count').notNull().default(0),
    requestedByUserId: uuid('requested_by_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
    emailLogId: uuid('email_log_id').references(() => accountingExportEmailLog.id, {
      onDelete: 'cascade',
    }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index('accounting_export_runs_tenant_created_idx').on(t.tenantId, t.createdAt.desc()),
    uniqueIndex('accounting_export_runs_email_log_uq')
      .on(t.emailLogId)
      .where(sql`email_log_id IS NOT NULL`),
    pgPolicy('accounting_export_runs_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
      withCheck: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export const accountingExportRunInvoices = pgTable(
  'accounting_export_run_invoices',
  {
    runId: uuid('run_id')
      .notNull()
      .references(() => accountingExportRuns.id, { onDelete: 'cascade' }),
    invoiceId: uuid('invoice_id')
      .notNull()
      .references(() => invoices.id, { onDelete: 'cascade' }),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
  },
  (t) => [
    primaryKey({ columns: [t.runId, t.invoiceId] }),
    index('accounting_export_run_invoices_tenant_invoice_idx').on(t.tenantId, t.invoiceId),
    pgPolicy('accounting_export_run_invoices_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
      withCheck: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type AccountingExportRun = typeof accountingExportRuns.$inferSelect;
export type AccountingExportRunInvoice = typeof accountingExportRunInvoices.$inferSelect;
